import BigLoader from '@components/BigLoader';
import Placeholder from '@components/Placeholder';
import ServerErrorMessage from '@components/ServerErrorMessage';
import Wordmark from '@components/Wordmark';
import { useAuth } from '@context/AuthContext';
import isBrowser from '@helpers/isBrowser';
import useTranslation from '@helpers/useTranslation';
import { Link, navigate } from 'gatsby';
import React, { FC, Suspense, useEffect } from 'react';
import { Columns, Container, Navbar, Section } from 'react-bulma-components';
import { ErrorBoundary } from 'react-error-boundary';
import { Helmet } from 'react-helmet';
import * as constants from '../../constants';
import * as styles from './DashboardTemplate.module.scss';
import DashboardTemplateMenu from './DashboardTemplateMenu';

const DashboardTemplate: FC = ({ children }) => {
  const { t } = useTranslation('dashboard');

  const { isAuthenticated, isReady, user } = useAuth();

  useEffect(() => {
    if (isReady && !isAuthenticated) {
      navigate(`${constants.LOGIN_PAGE}?continueTo=${window.location.pathname}`);
    }
  }, [isAuthenticated, isReady]);

  const handleErrorBoundaryReset = () => {
    window.location.reload();
  };

  return (
    <>
      <Helmet htmlAttributes={{ class: styles.background }} />
      <Navbar renderAs="header">
        <Container>
          <Navbar.Brand>
            <Navbar.Item renderAs={Link} to={constants.DASHBOARD_PAGE} title={t('backToDashboard')}>
              <Wordmark width={78} />
            </Navbar.Item>
          </Navbar.Brand>
          <Navbar.Container align="right" mobile={{ display: 'hidden' }}>
            <Navbar.Item renderAs="div">
              {isAuthenticated ? user?.email : <Placeholder size={160} />}
            </Navbar.Item>
          </Navbar.Container>
        </Container>
      </Navbar>
      <Section>
        <Container>
          <Columns>
            <Columns.Column size={3}>
              <DashboardTemplateMenu />
            </Columns.Column>
            <Columns.Column className={styles.content} renderAs="main" size={9}>
              <ErrorBoundary
                fallback={
                  <ServerErrorMessage showResetButton onResetClick={handleErrorBoundaryReset} />
                }
              >
                {isBrowser && isAuthenticated ? (
                  <Suspense fallback={<BigLoader />}>{children}</Suspense>
                ) : (
                  <BigLoader />
                )}
              </ErrorBoundary>
            </Columns.Column>
          </Columns>
        </Container>
      </Section>
    </>
  );
};

export default DashboardTemplate;
